import { useTranslation } from 'react-i18next';
import Center from 'raydiant-elements/layout/Center';
import CircularProgress from 'raydiant-elements/core/CircularProgress';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useState, useEffect } from 'react';
import { PresentationsList } from '@raydiant/api-client-js';

import SelectOption from '../../../components/Select/SelectOption';
import Select from '../../../components/Select/Select';
import usePresentationsList from '../../../hooks/usePresentationsList';

type ContentSelectProps = Omit<JSX.IntrinsicElements['select'], 'onChange'> & {
  onChange: (value: string) => void;
};

const ContentSelect = ({ onChange, ...props }: ContentSelectProps) => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = usePresentationsList();
  const [isSelectorClosed, setIsSelectorClosed] = useState(true);
  const [itemsForDisplay, setItemsForDisplay] = useState<PresentationsList[]>(
    [],
  );
  const [hasMoreData, setHasMoreData] = useState<boolean>(true);
  const [page, setPage] = useState(1);
  const itemsPerPage = 50;

  useEffect(() => {
    if (data) {
      setItemsForDisplay(data.slice(0, itemsPerPage));
    }
  }, [data]);

  useEffect(() => {
    if (isSelectorClosed && page !== 1 && data) {
      setItemsForDisplay(data.slice(0, itemsPerPage));
      setPage(1);
      setHasMoreData(true);
    }
  }, [isSelectorClosed, page, data]);

  const fetchMoreData = () => {
    if (data === undefined) return;
    if (isSelectorClosed) return;
    if (itemsForDisplay.length >= data.length) {
      setHasMoreData(false);
      return;
    }
    setItemsForDisplay((prevItems) => [
      ...prevItems,
      ...data.slice(page * itemsPerPage, (page + 1) * itemsPerPage),
    ]);
    setPage(page + 1);
  };

  const getPlaceholder = data?.find((item) => item.id === props.value);

  return (
    <Select
      disableMobile
      onChange={(value) => typeof value === 'string' && onChange(value)}
      wrapperClassName="min-w-[70%] max-w-[70%] md:min-w-[360px] md:max-w-[360px] mt-5 xl:ml-4 xl:mt-0"
      itemsClassName="mt-2 right-0 max-h-fit md:min-w-[500px] md:max-w-[500px] drop-shadow-xl"
      buttonClassName="bg-white"
      labelClassName="text-black font-semibold"
      setIsSelectorClosed={setIsSelectorClosed}
      renderAnchorElement={() => (
        <div
          className={`w-[90%] text-ellipsis overflow-hidden whitespace-nowrap ${
            getPlaceholder?.name && 'text-blue-600'
          }`}
        >
          {getPlaceholder?.name ??
            t('analytics.contentDetailPage.contentSelector.placeholder')}
        </div>
      )}
      {...props}
    >
      {isLoading && !isError && (
        <Center className="h-20">
          <CircularProgress size={30}></CircularProgress>
        </Center>
      )}
      {isError && (
        <div className="text-center text-red-600 py-3">
          {t('analytics.contentDetailPage.contentSelector.error')}
        </div>
      )}
      {!isLoading && !isError && (
        <InfiniteScroll
          dataLength={itemsForDisplay.length}
          next={fetchMoreData}
          hasMore={hasMoreData}
          height={400}
          loader={undefined}
        >
          {itemsForDisplay.map((item, key) => (
            <SelectOption
              key={key}
              onClick={() => onChange(item.id)}
              value={item.id}
              disableMobile
            >
              {item.name}
            </SelectOption>
          ))}
        </InfiniteScroll>
      )}
    </Select>
  );
};

export default ContentSelect;
